/**
 * TierTypography Components
 *
 * Tier-adaptive typography that responds to user's subscription tier:
 * - Layer 1: Inter font family
 * - Layer 2: Neue Haas Grotesk Display for headlines, Inter for body
 * - Layer 3: Miller Display for headlines, Inter for body
 *
 * Components:
 * - TierHeadline: Display text (h1-h6 equivalent)
 * - TierBody: Body text (p, span)
 * - NarratorText: Courier New for "screenwriting" voice (tier-invariant)
 *
 * Usage:
 * ```tsx
 * import { TierHeadline, TierBody, NarratorText } from '@dramadetector/ui';
 *
 * <TierHeadline size="xl" weight="bold" align="center" as="h1">
 *   Your Drama Score
 * </TierHeadline>
 *
 * <TierBody size="base" color="text-gray-700">
 *   Analyzing your communication patterns...
 * </TierBody>
 *
 * <NarratorText>"Analyzing Act 2 escalation patterns..."</NarratorText>
 * ```
 */
import React from 'react';
import { Text } from 'react-native';

export interface TierHeadlineProps {
  /** Text size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

  /** Font weight */
  weight?: 'normal' | 'semibold' | 'bold';

  /** Text alignment */
  align?: 'left' | 'center' | 'right';

  /** Text color (Tailwind class) */
  color?: string;

  /** Semantic HTML heading level (web only) */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /** Headline content */
  children: React.ReactNode;

  /** Additional Tailwind classes */
  className?: string;

  /** Inline styles (fallback for web when Tailwind classes don't apply) */
  style?: any;

  /** Test ID for E2E testing */
  testID?: string;
}

export function TierHeadline({
  size = 'xl',
  weight = 'bold',
  align = 'left',
  color,
  as,
  children,
  className = '',
  style,
  testID,
}: TierHeadlineProps) {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
  };

  const weightClasses = {
    normal: 'font-normal',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const colorClass = color || 'text-dark-gray';

  return (
    <Text
      className={`font-display ${sizeClasses[size]} ${weightClasses[weight]} ${alignClasses[align]} ${colorClass} ${className}`}
      style={style}
      testID={testID}
      accessibilityRole={as ? 'header' : 'text'}
      {...(as ? { 'aria-level': parseInt(as.replace('h', '')) } : {})}
    >
      {children}
    </Text>
  );
}

export interface TierBodyProps {
  /** Text size */
  size?: 'xs' | 'sm' | 'base' | 'lg';

  /** Font weight */
  weight?: 'normal' | 'medium' | 'semibold';

  /** Text alignment */
  align?: 'left' | 'center' | 'right';

  /** Text color (Tailwind class) */
  color?: string;

  /** Body content */
  children: React.ReactNode;

  /** Additional Tailwind classes */
  className?: string;

  /** Inline styles (fallback for web when Tailwind classes don't apply) */
  style?: any;

  /** Test ID for E2E testing */
  testID?: string;
}

export function TierBody({
  size = 'base',
  weight = 'normal',
  align = 'left',
  color,
  children,
  className = '',
  style,
  testID,
}: TierBodyProps) {
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
  };

  const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const colorClass = color || 'text-dark-gray';

  return (
    <Text
      className={`font-body ${sizeClasses[size]} ${weightClasses[weight]} ${alignClasses[align]} leading-normal ${colorClass} ${className}`}
      style={style}
      testID={testID}
      accessibilityRole="text"
    >
      {children}
    </Text>
  );
}

export interface NarratorTextProps {
  /** Text size */
  size?: 'xs' | 'sm' | 'base' | 'lg';

  /** Text alignment */
  align?: 'left' | 'center' | 'right';

  /** Text color (Tailwind class) */
  color?: string;

  /** Narrator content */
  children: React.ReactNode;

  /** Additional Tailwind classes */
  className?: string;

  /** Test ID for E2E testing */
  testID?: string;
}

/**
 * NarratorText Component (Tier-Invariant)
 *
 * Uses Courier New for "screenwriting" voice that frames the user's life
 * as episodic content. This is separate from tier-adaptive typography.
 *
 * Use for:
 * - Executive Producer loading quips
 * - "Previously on My Life" episode framing
 * - Shareable card narration
 * - Pattern observation narration
 *
 * Don't use for:
 * - Drama scores/results (use tier-adaptive fonts)
 * - Improvement suggestions (Drama Expert voice)
 * - User input UI
 * - Navigation/CTAs
 */
export function NarratorText({
  size = 'base',
  align = 'left',
  color,
  children,
  className = '',
  testID,
}: NarratorTextProps) {
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const colorClass = color || 'text-medium-gray';

  return (
    <Text
      className={`font-narrator ${sizeClasses[size]} ${alignClasses[align]} leading-relaxed ${colorClass} ${className}`}
      testID={testID}
      accessibilityRole="text"
    >
      {children}
    </Text>
  );
}
